import React from 'react';

import type { PreviousWeeklyUpdateRecord } from '@/api/project-details/mocks/projectHome.types';
import { appRoutes, navigateTo, type AppRouteKey } from '@/app/routes';
import { AssessmentLibrary } from '@/components/assessment/AssessmentLibrary';
import type { AssessmentFlowStep } from '@/components/assessment/AssessmentStepPager';
import { AnalysisPageRouter } from '@/components/pages/assessments/AnalysisPageRouter';
import { InterviewPageRouter } from '@/components/pages/assessments/InterviewPageRouter';
import { AssetLibraryPage } from '@/components/pages/AssetLibraryPage';
import ClientProjectsPage from '@/components/pages/ClientProjectsPage';
import { CreateNotePage } from '@/components/pages/CreateNotePage';
import { FileCabinetPage } from '@/components/pages/FileCabinetPage';
import { PortfolioDashboardPage } from '@/components/pages/PortfolioDashboardPage';
import { ProjectHomePage } from '@/components/pages/ProjectHomePage';
import { SkillEditorPage } from '@/components/pages/SkillEditorPage';
import { SkillsPage } from '@/components/pages/SkillsPage';
import { WeeklyUpdatePage } from '@/components/pages/WeeklyUpdatePage';
import { useProjectWeeklyStatusPermissions } from '@/hooks/useProjectWeeklyStatusPermissions';
import type { AuthenticatedUser } from '@/models/auth.model';
import { getCurrentWeekMonday, isWeekWithinEditWindow } from '@/utils/weeklyUpdateUtils';

export interface PageRendererProps {
  routeKey: AppRouteKey;
  assessmentInstanceId?: string;
  assetSessionId?: string;
  week?: string;
  skillTemplateKey?: string;
  skillVersionId?: string;
  effectiveClientId: string;
  effectiveProjectId: string;
  selectedClientId: string | undefined;
  selectedProjectId: string | undefined;
  accessToken: string | null | undefined;
  microsoftOboAssertion: string | null | undefined;
  projectSharepointReady: boolean | null;
  clientLabel: string;
  assessmentLabel: string;
  selectedProjectName: string | null;
  user: AuthenticatedUser | null;
  startSso: () => Promise<void>;
  refreshMicrosoftOboAssertion: () => Promise<string | null>;
  previousWeeklyUpdate: PreviousWeeklyUpdateRecord | null;
  setPreviousWeeklyUpdate: (update: PreviousWeeklyUpdateRecord | null) => void;
  navigateAssessmentFlowStep: (instanceId: string, step: AssessmentFlowStep) => void;
  onClientNamesResolved: (names: Record<string, string>) => void;
  onProjectNamesResolved: (names: Record<string, string>) => void;
  onProjectMetadataResolved: (
    clientId: string,
    projectId: string,
    names: { clientName: string; projectName: string },
  ) => void;
  clientNameById: Record<string, string>;
  onOpenAddAsset: () => void;
}

/** Permission a weekly-status route needs before it may render, or null if it needs none. */
function requiredWeeklyStatusPermission(
  routeKey: AppRouteKey,
): 'create' | 'update' | 'view' | null {
  switch (routeKey) {
    case 'project-weekly-update-create':
    case 'project-weekly-updates':
      return 'create';
    case 'project-weekly-update-edit':
      return 'update';
    case 'project-weekly-update-view':
      return 'view';
    default:
      return null;
  }
}

/**
 * Renders the correct page component for the current matched route. Owns the full workspace
 * and assessment switch logic previously inlined in AuthenticatedApp.
 */
export const PageRenderer: React.FC<PageRendererProps> = ({
  routeKey,
  assessmentInstanceId,
  assetSessionId,
  week,
  skillTemplateKey,
  skillVersionId,
  effectiveClientId,
  effectiveProjectId,
  selectedClientId,
  selectedProjectId,
  accessToken,
  microsoftOboAssertion,
  projectSharepointReady,
  clientLabel,
  assessmentLabel,
  selectedProjectName,
  user,
  startSso,
  refreshMicrosoftOboAssertion,
  previousWeeklyUpdate,
  setPreviousWeeklyUpdate,
  navigateAssessmentFlowStep,
  onClientNamesResolved,
  onProjectNamesResolved,
  onProjectMetadataResolved,
  clientNameById,
  onOpenAddAsset,
}) => {
  const isWeeklyStatusContext =
    routeKey === 'project-detail' ||
    routeKey === 'project-weekly-update-create' ||
    routeKey === 'project-weekly-update-edit' ||
    routeKey === 'project-weekly-update-view' ||
    routeKey === 'project-weekly-updates';
  const { permissions, loading: permissionsLoading } = useProjectWeeklyStatusPermissions(
    accessToken,
    effectiveClientId,
    effectiveProjectId,
    isWeeklyStatusContext,
  );
  const canCreateWeeklyStatus = permissions?.can_create_weekly_status === true;
  const canUpdateWeeklyStatus = permissions?.can_update_weekly_status === true;
  const canViewWeeklyStatus = permissions?.can_view_weekly_status === true;

  // Direct-URL guard. Landing on a weekly-status route without the permission it needs
  // sends the user back to the project page instead of leaving them on a blank screen.
  // Waits for permissionsLoading to settle so the redirect never fires on a pending fetch;
  // a failed fetch resolves to DENIED, which correctly redirects rather than exposing the page.
  const requiredPermission = requiredWeeklyStatusPermission(routeKey);
  const grantedPermission =
    requiredPermission === null ||
    (requiredPermission === 'create' && canCreateWeeklyStatus) ||
    (requiredPermission === 'update' && canUpdateWeeklyStatus) ||
    (requiredPermission === 'view' && canViewWeeklyStatus);
  const shouldRedirectUnauthorized = !permissionsLoading && !grantedPermission;

  React.useEffect(() => {
    if (shouldRedirectUnauthorized) {
      navigateTo(appRoutes.projectHome(effectiveClientId, effectiveProjectId));
    }
  }, [shouldRedirectUnauthorized, effectiveClientId, effectiveProjectId]);

  const getAssessmentStepPath = (instanceId: string, step: AssessmentFlowStep): string => {
    const stepRoutes: Record<
      AssessmentFlowStep,
      (id: string, project: string, client: string) => string
    > = {
      1: appRoutes.projectAssessmentStep1,
      '1-review': appRoutes.projectAssessmentStep1Review,
      2: appRoutes.projectAssessmentStep2,
      '2-generating': appRoutes.projectAssessmentStep2Generating,
      '2-review': appRoutes.projectAssessmentStep2Output,
    };
    return stepRoutes[step](instanceId, effectiveProjectId, effectiveClientId);
  };

  switch (routeKey) {
    case 'portfolio':
      return (
        <PortfolioDashboardPage
          onClientNamesResolved={onClientNamesResolved}
          onProjectNamesResolved={onProjectNamesResolved}
        />
      );

    case 'projects':
      return (
        <ClientProjectsPage
          clientId={selectedClientId ?? ''}
          clientNameById={clientNameById}
          onClientNamesResolved={onClientNamesResolved}
          onProjectNamesResolved={onProjectNamesResolved}
        />
      );

    case 'project-detail':
      return (
        <ProjectHomePage
          clientId={effectiveClientId}
          projectId={effectiveProjectId}
          clientLabel={clientLabel}
          accessToken={accessToken ?? ''}
          microsoftOboAssertion={microsoftOboAssertion}
          onOpenAssessment={(instanceId, flowStep) =>
            navigateTo(getAssessmentStepPath(instanceId, flowStep))
          }
          onNewAssessment={() => {
            const id = globalThis.crypto.randomUUID();
            navigateTo(appRoutes.projectAssessmentStep1(id, effectiveProjectId, effectiveClientId));
          }}
          onOpenFileCabinet={() =>
            navigateTo(appRoutes.projectAssessments(effectiveProjectId, effectiveClientId))
          }
          onOpenWorkspace={() =>
            navigateTo(appRoutes.projectAssetLibrary(effectiveProjectId, effectiveClientId))
          }
          onOpenWeeklyUpdate={(update) => {
            setPreviousWeeklyUpdate(update);
            if (update?.weekOf) {
              const isWithinEditWindow = isWeekWithinEditWindow(update.weekOf, false);

              if (isWithinEditWindow && canUpdateWeeklyStatus) {
                navigateTo(
                  appRoutes.projectWeeklyUpdateEdit(
                    effectiveClientId,
                    effectiveProjectId,
                    update.weekOf,
                  ),
                );
              } else {
                navigateTo(
                  appRoutes.projectWeeklyUpdateView(
                    effectiveClientId,
                    effectiveProjectId,
                    update.weekOf,
                  ),
                );
              }
            } else {
              // Creating new weekly update for current week
              navigateTo(
                appRoutes.projectWeeklyUpdateCreate(
                  effectiveClientId,
                  effectiveProjectId,
                  getCurrentWeekMonday(),
                ),
              );
            }
          }}
          onProjectMetadataResolved={onProjectMetadataResolved}
        />
      );

    case 'project-weekly-update-create': {
      if (permissionsLoading || !canCreateWeeklyStatus) {
        return <></>;
      }

      const isWeekEditableCreate = isWeekWithinEditWindow(week ?? '', true);

      return (
        <WeeklyUpdatePage
          mode={canCreateWeeklyStatus && isWeekEditableCreate ? 'create' : 'view'}
          week={week ?? ''}
          clientId={effectiveClientId}
          projectId={effectiveProjectId}
          clientLabel={clientLabel}
          projectName={selectedProjectName ?? 'Project'}
          canEditHealth={canCreateWeeklyStatus && isWeekEditableCreate}
          onBack={() => navigateTo(appRoutes.projectHome(effectiveClientId, effectiveProjectId))}
        />
      );
    }

    case 'project-weekly-update-edit': {
      if (permissionsLoading || !canUpdateWeeklyStatus) {
        return <></>;
      }

      const isWeekEditableRoute = isWeekWithinEditWindow(week ?? '', false);

      return (
        <WeeklyUpdatePage
          mode={canUpdateWeeklyStatus && isWeekEditableRoute ? 'edit' : 'view'}
          week={week ?? ''}
          clientId={effectiveClientId}
          projectId={effectiveProjectId}
          clientLabel={clientLabel}
          projectName={selectedProjectName ?? 'Project'}
          previousWeeklyUpdate={previousWeeklyUpdate}
          onBack={() => navigateTo(appRoutes.projectHome(effectiveClientId, effectiveProjectId))}
          canEditHealth={canUpdateWeeklyStatus && isWeekEditableRoute}
        />
      );
    }

    case 'project-weekly-update-view':
      if (permissionsLoading || !permissions?.can_view_weekly_status) {
        return <></>;
      }
      return (
        <WeeklyUpdatePage
          mode="view"
          week={week ?? ''}
          clientId={effectiveClientId}
          projectId={effectiveProjectId}
          clientLabel={clientLabel}
          projectName={selectedProjectName ?? 'Project'}
          previousWeeklyUpdate={previousWeeklyUpdate}
          onBack={() => navigateTo(appRoutes.projectHome(effectiveClientId, effectiveProjectId))}
        />
      );

    case 'project-weekly-updates':
      if (permissionsLoading || !canCreateWeeklyStatus) {
        return <></>;
      }
      return (
        <WeeklyUpdatePage
          clientId={effectiveClientId}
          projectId={effectiveProjectId}
          clientLabel={clientLabel}
          projectName={selectedProjectName ?? 'Project'}
          canEditHealth={canCreateWeeklyStatus}
          onBack={() => navigateTo(appRoutes.projectHome(effectiveClientId, effectiveProjectId))}
        />
      );

    case 'project-assessments':
      return (
        <AssessmentLibrary
          clientId={effectiveClientId}
          projectId={effectiveProjectId}
          accessToken={accessToken ?? ''}
          onOpenAssessment={(instanceId, flowStep) =>
            navigateTo(getAssessmentStepPath(instanceId, flowStep))
          }
          onNewAssessment={() => {
            const id = globalThis.crypto.randomUUID();
            navigateTo(appRoutes.projectAssessmentStep1(id, effectiveProjectId, effectiveClientId));
          }}
        />
      );

    case 'file-cabinet':
      return (
        <FileCabinetPage
          clientId={effectiveClientId}
          projectId={effectiveProjectId}
          accessToken={accessToken ?? ''}
          onOpenStep1={(instanceId) =>
            navigateTo(
              appRoutes.projectAssessmentStep1(instanceId, effectiveProjectId, effectiveClientId),
            )
          }
          onOpenWorkspace={() =>
            navigateTo(appRoutes.projectAssetLibrary(effectiveProjectId, effectiveClientId))
          }
        />
      );

    case 'asset-library':
      return (
        <AssetLibraryPage
          clientId={effectiveClientId}
          projectId={selectedProjectId}
          accessToken={accessToken}
          onOpenAsset={(cardId) =>
            navigateTo(
              appRoutes.projectAssetLibrarySession(cardId, effectiveProjectId, effectiveClientId),
            )
          }
          onOpenAddAsset={onOpenAddAsset}
        />
      );

    case 'asset-library-note':
      return (
        <CreateNotePage
          assetSessionId={assetSessionId ?? ''}
          accessToken={accessToken}
          clientId={effectiveClientId}
          projectId={effectiveProjectId}
          onBackToAssetLibrary={() =>
            navigateTo(appRoutes.projectAssetLibrary(effectiveProjectId, effectiveClientId))
          }
        />
      );

    case 'skills':
      return (
        <SkillsPage
          accessToken={accessToken}
          onOpenTemplate={(templateKey) => navigateTo(appRoutes.skillTemplate(templateKey))}
          onOpenSkillVersion={(vid) => navigateTo(appRoutes.skillEdit(vid))}
          onCreateNew={() => navigateTo(appRoutes.skillEdit('new'))}
        />
      );

    case 'skill-template':
      return (
        <SkillEditorPage
          accessToken={accessToken}
          mode="template"
          templateKey={skillTemplateKey ?? ''}
        />
      );

    case 'skill-edit': {
      const vid = skillVersionId ?? 'new';
      return (
        <SkillEditorPage
          accessToken={accessToken}
          mode={vid === 'new' ? 'new' : 'version'}
          versionId={vid === 'new' ? undefined : vid}
        />
      );
    }

    case 'step-1':
    case 'step-1-generating':
    case 'step-1-output':
    case 'step-1-review':
    case 'step-2':
    case 'step-2-generating':
    case 'step-2-output':
      return (
        <InterviewPageRouter
          routeKey={routeKey}
          assessmentInstanceId={assessmentInstanceId}
          effectiveClientId={effectiveClientId}
          effectiveProjectId={effectiveProjectId}
          accessToken={accessToken ?? null}
          microsoftOboAssertion={microsoftOboAssertion ?? null}
          projectSharepointReady={projectSharepointReady}
          clientLabel={clientLabel}
          assessmentLabel={assessmentLabel}
          onStartSso={startSso}
          onRefreshMicrosoftObo={refreshMicrosoftOboAssertion}
          onNavigateAssessmentStep={navigateAssessmentFlowStep}
        />
      );

    default:
      return (
        <AnalysisPageRouter routeKey={routeKey} accessToken={accessToken ?? null} user={user} />
      );
  }
};
